import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { notificationPrefsApi, monitoredAreasApi, apiErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { PushDeniedHint } from "@/components/NotificationPermissionPrompt";

const BUCKETS = [["high", "High only"], ["medium", "High + Medium"], ["low", "High + Medium + Low"], ["unranked", "Everything"]];
const RATINGS = ["5", "4", "3", "2", "1", "0"];

export default function NotificationPreferences() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [areas, setAreas] = useState([]);
  const [newPostcode, setNewPostcode] = useState("");
  const [newRadius, setNewRadius] = useState(5);

  const load = async () => {
    const [p, a] = await Promise.all([notificationPrefsApi.get(), monitoredAreasApi.list()]);
    setPrefs(p.data); setAreas(a.data.items);
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    try { const { data } = await notificationPrefsApi.update(patch); setPrefs(data); toast.success("Preferences saved"); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const addPostcodeScope = () => {
    if (!newPostcode) return;
    const scopes = [...(prefs.geographic_scopes || []), { kind: "postcode_radius", postcode: newPostcode, radius_km: Number(newRadius) }];
    save({ geographic_scopes: scopes });
    setNewPostcode("");
  };
  const removeScope = (i) => save({ geographic_scopes: prefs.geographic_scopes.filter((_, idx) => idx !== i) });
  const toggleRating = (r) => {
    const cur = prefs.allowed_fhrs_ratings || [];
    save({ allowed_fhrs_ratings: cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r] });
  };

  if (!prefs) return <div className="mx-auto max-w-2xl px-4 py-6"><div className="h-40 animate-pulse rounded-xl border border-slate-200 bg-white" /></div>;
  const noScope = !(prefs.geographic_scopes || []).length;

  return (
    <div className="mx-auto max-w-2xl px-4 py-5 md:py-8" data-testid="notification-preferences-page">
      <h1 className="mb-4 font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Notification preferences</h1>
      <PushDeniedHint />

      {noScope && (
        <div data-testid="no-scope-warning" className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>You will not receive alerts until you add at least one area below.</span>
        </div>
      )}

      <Card title="Alert types">
        <Toggle testid="pref-prospect-toggle" label="New CaterCTRL prospects" checked={prefs.new_caterctrl_prospect_enabled}
          onChange={(v) => save({ new_caterctrl_prospect_enabled: v })} />
        <Toggle testid="pref-rating-toggle" label="FHRS rating changes" sub="Off by default to reduce noise" checked={prefs.rating_change_enabled}
          onChange={(v) => save({ rating_change_enabled: v })} />
      </Card>

      <Card title="Minimum priority">
        <select data-testid="pref-bucket-select" value={prefs.min_priority_bucket} onChange={(e) => save({ min_priority_bucket: e.target.value })}
          className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm">
          {BUCKETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Card>

      <Card title="Allowed FHRS ratings" sub="Empty = any rating">
        <div className="flex flex-wrap gap-1.5" data-testid="pref-ratings">
          {RATINGS.map((r) => (
            <button key={r} data-testid={`pref-rating-${r}`} onClick={() => toggleRating(r)}
              className={`h-10 w-10 rounded-lg border text-sm font-bold ${(prefs.allowed_fhrs_ratings || []).includes(r) ? "border-teal-600 bg-teal-600 text-white" : "border-slate-200 text-slate-600"}`}>{r}</button>
          ))}
        </div>
      </Card>

      <Card title="My areas" sub="Alerts only fire for establishments inside these areas">
        <div className="space-y-2" data-testid="pref-scopes">
          {(prefs.geographic_scopes || []).map((s, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <span className="font-medium text-slate-700">
                {s.kind === "postcode_radius" ? `${s.postcode} · ${s.radius_km}km` : s.kind === "bbox" ? "Map area" : "Monitored area"}
              </span>
              <button data-testid={`pref-scope-remove-${i}`} onClick={() => removeScope(i)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input data-testid="pref-new-postcode" value={newPostcode} onChange={(e) => setNewPostcode(e.target.value)} placeholder="Postcode e.g. BS1 4DJ"
            className="min-h-[44px] flex-1 rounded-lg border border-slate-200 px-3 text-sm" />
          <input data-testid="pref-new-radius" type="number" min="1" value={newRadius} onChange={(e) => setNewRadius(e.target.value)}
            className="min-h-[44px] w-20 rounded-lg border border-slate-200 px-3 text-sm" />
          <button data-testid="pref-add-scope" onClick={addPostcodeScope} className="flex min-h-[44px] items-center gap-1 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white hover:bg-teal-800"><Plus className="h-4 w-4" /> Add</button>
        </div>
        {areas.length > 0 && (
          <p className="mt-2 text-xs text-slate-400">Tip: managers can define shared monitored areas. <button onClick={() => navigate("/territories")} className="font-semibold text-teal-700">Manage</button></p>
        )}
      </Card>
    </div>
  );
}

function Card({ title, sub, children }) {
  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="font-heading text-sm font-bold text-slate-800">{title}</h2>
      {sub && <p className="mb-2 text-xs text-slate-400">{sub}</p>}
      <div className={sub ? "" : "mt-2"}>{children}</div>
    </div>
  );
}
function Toggle({ testid, label, sub, checked, onChange }) {
  return (
    <label className="flex items-center justify-between py-2">
      <span><span className="text-sm font-medium text-slate-700">{label}</span>{sub && <span className="block text-xs text-slate-400">{sub}</span>}</span>
      <button data-testid={testid} onClick={() => onChange(!checked)} className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-teal-600" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </label>
  );
}
